import { View } from '@react-pdf/renderer';
import type { Style } from '@react-pdf/types';
import type { ReactElement } from 'react';

import type { Quote } from '@core/types/api/Quote/Quote';
import { randomUUID } from '@core/utils/uuid';

import { PdfHeader } from '../../../components/PdfHeader';
import { type PdfColumnConfig, PdfTable, pdfColumnFromConfig } from '../../../components/PdfTable';

const infoStyle: Style = {
  textAlign: 'left',
  paddingRight: '8pt',
};

const formatElementForPdf = (value: ReactElement): ReactElement => value;

type InfoRow = {
  id: string;
  value: string | null;
};

type HeaderRow = {
  id: string;
  leftValue: ReactElement;
  rightValue: ReactElement;
};

type PdfHeaderTableProps = {
  quote: Quote;
  locale: string;
};

export const PdfHeaderTable = ({ quote, locale }: PdfHeaderTableProps): ReactElement => {
  const logoImg = (
    <View style={{ alignItems: 'center' }}>
      <PdfHeader
        image={{
          src: '../lauren-logo.jpeg',
          imgStyle: {
            width: '1.8in',
            height: '1in',
          },
        }}
      />
    </View>
  );

  const projectLocation = [
    `${quote.project.address.basicDisplay()}`,
    quote.project.address.city,
    quote.project.address.state,
  ]
    .filter((s) => s !== '' && s !== null)
    .join(', ');

  const infoRows: InfoRow[] = [
    {
      id: randomUUID(),
      value: quote.company !== null ? `CUSTOMER: ${quote.company.name}` : '',
    },
    {
      id: randomUUID(),
      value: `PROJECT: ${quote.project.name}`,
    },
    {
      id: randomUUID(),
      value: `PROJECT LOCATION: ${projectLocation}`,
    },
    {
      id: randomUUID(),
      value: quote.creationDate === null ? null : `DATE: ${quote.creationDateDisplay(locale)}`,
    },
    {
      id: randomUUID(),
      value: quote.revisionDate === null ? null : `REVISED: ${quote.revisionDateDisplay(locale)}`,
    },
  ];

  const infoCols: PdfColumnConfig<InfoRow>[] = [
    {
      id: 'value',
      label: '',
      type: 'string',
      isDisplayed: true,
      widthOverride: '100%',
      rowViewStyle: infoStyle,
    },
  ];

  const infoTable = <PdfTable columns={infoCols.map(pdfColumnFromConfig)} rows={infoRows} />;

  const tableRows: HeaderRow[] = [
    {
      id: randomUUID(),
      leftValue: logoImg,
      rightValue: infoTable,
    },
  ];

  const tableCols: PdfColumnConfig<HeaderRow>[] = [
    {
      id: 'leftValue',
      label: '',
      type: 'custom',
      isDisplayed: false,
      formatValueForPdf: formatElementForPdf as (
        value: ReactElement | string | null,
      ) => ReactElement,
      widthOverride: '40%',
    },
    {
      id: 'rightValue',
      label: '',
      type: 'custom',
      isDisplayed: false,
      formatValueForPdf: formatElementForPdf as (
        value: ReactElement | string | null,
      ) => ReactElement,
      widthOverride: '60%',
    },
  ];

  return (
    <PdfTable columns={tableCols.map(pdfColumnFromConfig)} rows={tableRows} cellPadding='tight' />
  );
};
